/**
 * Timing System — per-card UI panel
 *
 * Lightweight time-of-day platform override. Toggle OFF by default.
 * Only changes the redirect destination; nothing else.
 *
 * Props:
 *   timingEnabled, setTimingEnabled, timingSchedule, setTimingSchedule
 *   selectedCard         (full card row for bulk-apply context)
 *   t                    (i18n)
 */
import { useState, useMemo } from 'react';
import { Plus, Trash2, Clock, Send, Loader2, Check, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import { analyzeSchedule } from './timing/scheduleAnalyzer';
import TimelineStrip from './timing/TimelineStrip';
import ConflictBanner from './timing/ConflictBanner';
import PatternPresets from './timing/PatternPresets';
import DayGroupHelpers from './timing/DayGroupHelpers';

const PLATFORMS = [
    { value: 'google_reviews', label: 'Google' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'yelp', label: 'Yelp' },
    { value: 'tripadvisor', label: 'TripAdvisor' },
    { value: 'trustpilot', label: 'Trustpilot' },
];

const DAYS = [
    { code: 'mon', short: 'M' },
    { code: 'tue', short: 'T' },
    { code: 'wed', short: 'W' },
    { code: 'thu', short: 'T' },
    { code: 'fri', short: 'F' },
    { code: 'sat', short: 'S' },
    { code: 'sun', short: 'S' },
];

const ALL_DAYS = DAYS.map(d => d.code);

// v4.11.4 — minute helpers + auto-snap for tiny accidental overlaps.
const TINY_OVERLAP_TOLERANCE_MIN = 5;

const _toMin = (hhmm) => {
    if (!hhmm) return null;
    const [h, m] = hhmm.split(':').map(n => parseInt(n, 10));
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
};
const _toHHMM = (mins) => {
    const v = Math.max(0, Math.min(24 * 60 - 1, mins));
    return `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
};
const _dayLabel = (d) => ({ mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' }[d] || d);

// Returns { snapped, adjustments } where adjustments describe nudges that
// were applied for ≤ tolerance overlaps. Larger overlaps are left alone
// for the validator to surface with a suggestion.
function autoSnapSchedule(blocks, tolerance = TINY_OVERLAP_TOLERANCE_MIN) {
    if (!Array.isArray(blocks) || blocks.length === 0) {
        return { snapped: blocks || [], adjustments: [] };
    }
    const snapped = blocks.map(b => ({ ...b }));
    const adjustments = [];
    const byDay = {};
    snapped.forEach((b, i) => {
        const s = _toMin(b.start_time);
        const e = _toMin(b.end_time);
        if (s === null || e === null || s === e) return;
        if (s > e) return; // skip crossover; not auto-snapped
        (b.days || []).forEach(d => {
            const key = String(d).toLowerCase().slice(0, 3);
            (byDay[key] ||= []).push({ i, s, e });
        });
    });
    Object.entries(byDay).forEach(([day, items]) => {
        items.sort((a, b) => a.s - b.s || a.e - b.e);
        for (let j = 1; j < items.length; j++) {
            const prev = items[j - 1], curr = items[j];
            const overlap = prev.e - curr.s;
            if (overlap > 0 && overlap <= tolerance) {
                const newStart = prev.e;
                if (newStart >= curr.e) continue;
                const oldStr = snapped[curr.i].start_time;
                const newStr = _toHHMM(newStart);
                snapped[curr.i].start_time = newStr;
                adjustments.push({
                    block_index: curr.i + 1,
                    field: 'start_time',
                    old: oldStr,
                    new: newStr,
                    day,
                    neighbour_index: prev.i + 1,
                });
                curr.s = newStart;
            }
        }
    });
    return { snapped, adjustments };
}

// Friendly platform label (matches the same map used in Cards.jsx).
function fmtPlatform(value) {
    if (!value) return '';
    const m = {
        google_reviews: 'Google',
        facebook: 'Facebook',
        instagram: 'Instagram',
        trustpilot: 'Trustpilot',
        yelp: 'Yelp',
        tripadvisor: 'TripAdvisor',
    };
    return m[value] || value;
}

// Build a compact daily schedule summary for the read-only tooltip.
const DAY_ORDER_T = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_SHORT_T = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
function summarizeBlocksTooltip(schedule) {
    if (!Array.isArray(schedule) || schedule.length === 0) return '';
    const summarizeDays = (days) => {
        const set = new Set((days || []).map(d => String(d).toLowerCase().slice(0, 3)));
        if (set.size === 7) return 'Every day';
        return DAY_ORDER_T.filter(d => set.has(d)).map(d => DAY_SHORT_T[d]).join(', ');
    };
    return schedule
        .map(b => `${summarizeDays(b.days)} ${b.start_time || '?'}–${b.end_time || '?'} → ${fmtPlatform(b.platform)}`)
        .join('\n');
}

// Mirror of backend resolver — used only for the read-only indicator inside
// the Timing panel itself. The authoritative resolution still happens server
// side (resolve_active_platform). Kept tiny on purpose.
function clientResolveTiming({ enabled, schedule, legacyPlatform, tz }) {
    if (!enabled) return { platform: legacyPlatform, source: 'default' };
    const blocks = Array.isArray(schedule) ? schedule : [];
    if (!blocks.length) return { platform: legacyPlatform, source: 'default' };
    let now;
    try {
        // Build the current local HH:MM and weekday in the card's tz
        const fmt = new Intl.DateTimeFormat('en-GB', {
            timeZone: tz || 'America/New_York',
            hour: '2-digit', minute: '2-digit', hour12: false, weekday: 'short',
        });
        const parts = fmt.formatToParts(new Date()).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
        const day = (parts.weekday || '').toLowerCase().slice(0, 3); // mon..sun
        const hhmm = `${parts.hour}:${parts.minute}`;
        now = { day, hhmm };
    } catch {
        return { platform: legacyPlatform, source: 'default' };
    }
    for (const b of blocks) {
        const days = (b.days || []).map(d => String(d).toLowerCase().slice(0, 3));
        const start = (b.start_time || '').trim();
        const end = (b.end_time || '').trim();
        if (!start || !end || start === end) continue;
        const dayOrder = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
        const prevDay = dayOrder[(dayOrder.indexOf(now.day) + 6) % 7];
        if (start < end) {
            // Same-day window.
            if (days.length && !days.includes(now.day)) continue;
            if (start <= now.hhmm && now.hhmm < end) {
                return { platform: b.platform || legacyPlatform, source: 'block' };
            }
        } else {
            // Midnight crossover (start > end). Evening segment fires on
            // listed days; morning segment fires when prev_day is listed.
            if ((!days.length || days.includes(now.day)) && now.hhmm >= start) {
                return { platform: b.platform || legacyPlatform, source: 'block' };
            }
            if ((!days.length || days.includes(prevDay)) && now.hhmm < end) {
                return { platform: b.platform || legacyPlatform, source: 'block' };
            }
        }
    }
    return { platform: legacyPlatform, source: 'default' };
}

function blankBlock() {
    return { start_time: '09:00', end_time: '17:00', days: [...ALL_DAYS], platform: 'google_reviews' };
}

export function TimingPanel({
    timingEnabled,
    setTimingEnabled,
    timingSchedule,
    setTimingSchedule,
    selectedCard,
    t,
}) {
    const [applyOpen, setApplyOpen] = useState(false);
    const [applyScope, setApplyScope] = useState('card');
    const [applying, setApplying] = useState(false);

    const blocks = Array.isArray(timingSchedule) ? timingSchedule : [];

    // v4.15.0 — Live conflict/issue analysis. Re-runs on every change.
    // The operator sees overlaps inline as they type instead of on save.
    const analysis = useMemo(
        () => analyzeSchedule(blocks, !!timingEnabled),
        [blocks, timingEnabled],
    );

    // Apply a smart-repair fix proposed by ConflictBanner.
    const applyFix = (fix) => {
        const idx = fix.target_index - 1;
        if (idx < 0 || idx >= blocks.length) return;
        const next = blocks.map((b, i) => {
            if (i !== idx) return b;
            if (fix.kind === 'set_start') return { ...b, start_time: fix.new_value };
            if (fix.kind === 'drop_day')  return { ...b, days: (b.days || []).filter(d => d !== fix.day) };
            return b;
        });
        setTimingSchedule(next);
        toast.success(fix.label);
    };

    // Replace the entire schedule from a Pattern Preset.
    const applyPreset = (newBlocks) => {
        setTimingSchedule(newBlocks);
        if (!timingEnabled) setTimingEnabled(true);
        toast.success(`Applied template — ${newBlocks.length} block${newBlocks.length === 1 ? '' : 's'} configured.`);
    };

    // Live read-only "Currently routing to" indicator (matches backend resolver).
    const live = clientResolveTiming({
        enabled: !!timingEnabled,
        schedule: blocks,
        legacyPlatform: selectedCard?.platform,
        tz: selectedCard?.schedule_timezone || 'America/New_York',
    });

    const updateBlock = (i, patch) => {
        const next = blocks.map((b, idx) => idx === i ? { ...b, ...patch } : b);
        setTimingSchedule(next);
    };
    const removeBlock = (i) => setTimingSchedule(blocks.filter((_, idx) => idx !== i));
    const addBlock = () => setTimingSchedule([...blocks, blankBlock()]);
    const toggleDay = (i, day) => {
        const b = blocks[i];
        const has = (b.days || []).includes(day);
        const next = has ? b.days.filter(d => d !== day) : [...(b.days || []), day];
        updateBlock(i, { days: next });
    };

    const localValidate = () => {
        if (!timingEnabled) return [];
        const errs = [];
        // Per-block field checks
        blocks.forEach((b, i) => {
            const idx = i + 1;
            if (!b.start_time || !b.end_time) errs.push(`Block #${idx}: time required`);
            else if (b.start_time === b.end_time) errs.push(`Block #${idx}: start and end cannot match`);
            if (!(b.days || []).length) errs.push(`Block #${idx}: pick at least one day`);
            if (!b.platform) errs.push(`Block #${idx}: pick a platform`);
        });

        // Overlap detection — actionable messages (matches backend wording).
        // Expand each block into (day, start_min, end_min) chunks. Crossover
        // blocks produce two chunks per listed day.
        const ranges = [];
        blocks.forEach((b, i) => {
            const s = _toMin(b.start_time);
            const e = _toMin(b.end_time);
            if (s === null || e === null || s === e) return;
            const days = (b.days || []).map(d => String(d).toLowerCase().slice(0, 3));
            const startStr = b.start_time;
            const endStr = b.end_time;
            const dayOrder = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
            days.forEach(d => {
                if (!dayOrder.includes(d)) return;
                if (s < e) {
                    ranges.push({ i: i + 1, day: d, s, e, platform: b.platform || '', startStr, endStr });
                } else {
                    // crossover — evening + next-day morning chunks
                    ranges.push({ i: i + 1, day: d, s, e: 24 * 60, platform: b.platform || '', startStr, endStr });
                    const next = dayOrder[(dayOrder.indexOf(d) + 1) % 7];
                    ranges.push({ i: i + 1, day: next, s: 0, e, platform: b.platform || '', startStr, endStr });
                }
            });
        });
        const byDay = {};
        ranges.forEach(r => { (byDay[r.day] ||= []).push(r); });
        Object.entries(byDay).forEach(([day, items]) => {
            const sorted = [...items].sort((a, b) => a.s - b.s);
            for (let k = 1; k < sorted.length; k++) {
                const a = sorted[k - 1], b = sorted[k];
                if (a.e > b.s && a.i !== b.i) {
                    const overlapMin = a.e - b.s;
                    const dur = overlapMin >= 60
                        ? (overlapMin % 60 === 0 ? `${Math.floor(overlapMin / 60)}h` : `${Math.floor(overlapMin / 60)}h ${overlapMin % 60}min`)
                        : `${overlapMin} min`;
                    const dayLbl = _dayLabel(day);
                    const aLbl = `Block #${a.i} (${dayLbl} ${a.startStr}–${a.endStr}${a.platform ? ' · ' + a.platform : ''})`;
                    const bLbl = `Block #${b.i} (${dayLbl} ${b.startStr}–${b.endStr}${b.platform ? ' · ' + b.platform : ''})`;
                    const suggested = _toHHMM(a.e);
                    errs.push(
                        `Overlap on ${dayLbl} (${dur}): ${bLbl} starts inside ${aLbl}. ` +
                        `Try changing Block #${b.i} start to ${suggested}, or remove ${dayLbl} from one of the two blocks.`
                    );
                }
            }
        });
        return errs;
    };

    const submitApply = async () => {
        // v4.11.4 — Auto-snap tiny accidental overlaps (≤ 5 min) BEFORE
        // validation. If a real day-level conflict remains, block the
        // save and surface every error with a concrete suggestion.
        let workingBlocks = blocks;
        const { snapped, adjustments } = autoSnapSchedule(workingBlocks);
        if (adjustments.length) {
            setTimingSchedule(snapped);
            workingBlocks = snapped;
            adjustments.forEach(a => {
                toast.success(
                    t(
                        'timing.autoSnapped',
                        `Auto-adjusted Block #${a.block_index} start ${a.old} → ${a.new} on ${_dayLabel(a.day)} to remove a tiny overlap with Block #${a.neighbour_index}.`,
                        {
                            block: a.block_index,
                            old: a.old,
                            new: a.new,
                            day: _dayLabel(a.day),
                            neighbour: a.neighbour_index,
                        }
                    )
                );
            });
        }
        // Re-run validation against the snapped schedule.
        const errs = (() => {
            if (!timingEnabled) return [];
            const tmpBlocks = workingBlocks;
            const out = [];
            tmpBlocks.forEach((b, i) => {
                const idx = i + 1;
                if (!b.start_time || !b.end_time) out.push(`Block #${idx}: time required`);
                else if (b.start_time === b.end_time) out.push(`Block #${idx}: start and end cannot match`);
                if (!(b.days || []).length) out.push(`Block #${idx}: pick at least one day`);
                if (!b.platform) out.push(`Block #${idx}: pick a platform`);
            });
            // Reuse the live validator function once state is set;
            // but `blocks` is stale here. Run inline overlap detection.
            const ranges = [];
            const dayOrder = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
            tmpBlocks.forEach((b, i) => {
                const s = _toMin(b.start_time);
                const e = _toMin(b.end_time);
                if (s === null || e === null || s === e) return;
                const days = (b.days || []).map(d => String(d).toLowerCase().slice(0, 3));
                days.forEach(d => {
                    if (!dayOrder.includes(d)) return;
                    if (s < e) ranges.push({ i: i + 1, day: d, s, e, platform: b.platform || '', startStr: b.start_time, endStr: b.end_time });
                    else {
                        ranges.push({ i: i + 1, day: d, s, e: 24 * 60, platform: b.platform || '', startStr: b.start_time, endStr: b.end_time });
                        const next = dayOrder[(dayOrder.indexOf(d) + 1) % 7];
                        ranges.push({ i: i + 1, day: next, s: 0, e, platform: b.platform || '', startStr: b.start_time, endStr: b.end_time });
                    }
                });
            });
            const byDay = {};
            ranges.forEach(r => { (byDay[r.day] ||= []).push(r); });
            Object.entries(byDay).forEach(([day, items]) => {
                const sorted = [...items].sort((a, b) => a.s - b.s);
                for (let k = 1; k < sorted.length; k++) {
                    const a = sorted[k - 1], bb = sorted[k];
                    if (a.e > bb.s && a.i !== bb.i) {
                        const overlapMin = a.e - bb.s;
                        const dur = overlapMin >= 60
                            ? (overlapMin % 60 === 0 ? `${Math.floor(overlapMin / 60)}h` : `${Math.floor(overlapMin / 60)}h ${overlapMin % 60}min`)
                            : `${overlapMin} min`;
                        const dayLbl = _dayLabel(day);
                        const aLbl = `Block #${a.i} (${dayLbl} ${a.startStr}–${a.endStr}${a.platform ? ' · ' + a.platform : ''})`;
                        const bLbl = `Block #${bb.i} (${dayLbl} ${bb.startStr}–${bb.endStr}${bb.platform ? ' · ' + bb.platform : ''})`;
                        const suggested = _toHHMM(a.e);
                        out.push(
                            `Overlap on ${dayLbl} (${dur}): ${bLbl} starts inside ${aLbl}. ` +
                            `Try changing Block #${bb.i} start to ${suggested}, or remove ${dayLbl} from one of the two blocks.`
                        );
                    }
                }
            });
            return out;
        })();
        if (errs.length) {
            // Surface every error so the operator never wonders what's wrong.
            errs.slice(0, 4).forEach(e => toast.error(e, { duration: 9000 }));
            return;
        }
        if (!selectedCard?.id) { toast.error(t('common.error', 'Error')); return; }
        setApplying(true);
        try {
            const API = window.location.origin;
            const token = localStorage.getItem('yulontap_token');
            const res = await fetch(`${API}/api/cards/timing/apply`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    timing_enabled: !!timingEnabled,
                    timing_schedule: workingBlocks,
                    scope: applyScope,
                    source_card_id: selectedCard.id,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const detail = data?.detail;
                const errsFromServer = (typeof detail === 'object' && Array.isArray(detail?.errors)) ? detail.errors : null;
                if (errsFromServer && errsFromServer.length) {
                    errsFromServer.slice(0, 4).forEach(e => toast.error(e, { duration: 9000 }));
                } else {
                    toast.error((typeof detail === 'string' ? detail : detail?.message) || `HTTP ${res.status}`);
                }
                setApplying(false);
                return;
            }
            // Surface any adjustments the backend made (defensive — should
            // already match the frontend ones, but production safety).
            if (Array.isArray(data?.adjustments) && data.adjustments.length) {
                data.adjustments.forEach(a => {
                    toast.success(
                        `Auto-adjusted Block #${a.block_index} start ${a.old} → ${a.new} on ${_dayLabel(a.day)} to remove a tiny overlap.`
                    );
                });
                if (Array.isArray(data?.timing_schedule)) setTimingSchedule(data.timing_schedule);
            }
            toast.success(t('timing.appliedToN', `Timing applied to ${data.updated_count} cards`, { n: data.updated_count }));
            setApplyOpen(false);
        } catch {
            toast.error(t('common.networkError', 'Network error'));
        } finally {
            setApplying(false);
        }
    };

    return (
        <div className="border-t pt-4 space-y-3" data-testid="timing-section" data-insight-id="fleet.schedule.editor" data-insight-label="Card schedule editor">
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <Label className="text-sm font-semibold flex items-center gap-2">
                        <Clock className="w-4 h-4 text-cyan-500" />
                        {t('timing.title', 'Timing')}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        {t(
                            'timing.desc',
                            'Change where this card sends guests based on time of day. Review Shield, Landing Page, and all other features behave exactly as they do today — only the destination URL may change.'
                        )}
                    </p>
                </div>
                <Switch
                    checked={!!timingEnabled}
                    onCheckedChange={(v) => {
                        setTimingEnabled(v);
                        toast.success(v
                            ? t('timing.onMsg', 'Scheduled routing is now active for this card.')
                            : t('timing.offMsg', 'Scheduled routing is disabled. Standard behavior is now active.')
                        );
                    }}
                    data-testid="timing-enable-toggle"
                    data-insight-id="fleet.toggle.static-vs-scheduled"
                    data-insight-label="Static vs Scheduled mode"
                />
            </div>

            {/* Read-only "Currently routing to" indicator — only shown when ON */}
            {timingEnabled && (
                <TooltipProvider delayDuration={150}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <p
                                className="text-[11px] text-slate-500 dark:text-slate-400 leading-tight cursor-help underline decoration-dotted decoration-slate-300 underline-offset-2 inline-block"
                                data-testid="timing-current-routing"
                                tabIndex={0}
                            >
                                {t('timing.currentlyRouting', 'Currently routing to')}:{' '}
                                <span className="font-medium text-slate-700 dark:text-slate-200">
                                    {live.source === 'block'
                                        ? fmtPlatform(live.platform)
                                        : t('timing.routingDefault', 'Default')}
                                </span>
                            </p>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[320px] whitespace-pre-line text-[11px] leading-relaxed">
                            <p className="font-semibold mb-1">{t('timing.dailySchedule', 'Daily schedule')}</p>
                            {blocks.length > 0
                                ? summarizeBlocksTooltip(blocks)
                                : t('timing.noBlocks', 'No time blocks — using default platform.')}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}

            <div
                className={
                    'rounded-lg border p-3 transition-opacity ' +
                    (timingEnabled
                        ? 'bg-gradient-to-br from-cyan-50/50 to-blue-50/50 dark:from-cyan-950/20 dark:to-blue-950/20'
                        : 'bg-slate-50 dark:bg-slate-900/40 opacity-60')
                }
                aria-disabled={!timingEnabled}
            >
                {blocks.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-2" data-testid="timing-empty">
                        {t('timing.empty', 'No time blocks yet. Pick a quick pattern below or add one manually.')}
                    </p>
                )}

                {/* v4.15.0 — Quick pattern presets (operators think in templates, not atoms) */}
                {timingEnabled && (
                    <div className="mb-3">
                        <PatternPresets
                            disabled={!timingEnabled}
                            hasBlocks={blocks.length > 0}
                            onApply={applyPreset}
                        />
                    </div>
                )}

                {/* v4.15.0 — Live daily timeline visualization */}
                {timingEnabled && blocks.length > 0 && (
                    <div className="mb-3">
                        <TimelineStrip analysis={analysis} blocks={blocks} />
                    </div>
                )}

                <div className="space-y-2">
                    {blocks.map((b, i) => {
                        const meta = analysis.blocks[i] || { conflicts: [], issues: [] };
                        const hasProblem = meta.conflicts.length > 0 || meta.issues.length > 0;
                        return (
                        <div
                            key={i}
                            className={
                                'rounded-md p-2.5 space-y-2 transition-all ' +
                                (hasProblem
                                    ? 'bg-white dark:bg-slate-900 border-2 border-red-400/70 dark:border-red-500/60 shadow-[0_0_0_3px_rgba(239,68,68,0.10)]'
                                    : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700')
                            }
                            data-testid={`timing-block-${i}`}
                            data-conflict={meta.conflicts.length > 0 ? 'true' : 'false'}
                            data-insight-id={i === 0 ? 'fleet.schedule.edit-block' : undefined}
                            data-insight-label={i === 0 ? 'Edit a timing block' : undefined}
                        >
                            <div className="flex items-center gap-2">
                                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500 w-6">
                                    #{i + 1}
                                </div>
                                <Input
                                    type="time"
                                    value={b.start_time || ''}
                                    onChange={(e) => updateBlock(i, { start_time: e.target.value })}
                                    disabled={!timingEnabled}
                                    className="font-mono text-sm w-[110px]"
                                    data-testid={`timing-start-${i}`}
                                />
                                <span className="text-xs text-muted-foreground">→</span>
                                <Input
                                    type="time"
                                    value={b.end_time || ''}
                                    onChange={(e) => updateBlock(i, { end_time: e.target.value })}
                                    disabled={!timingEnabled}
                                    className="font-mono text-sm w-[110px]"
                                    data-testid={`timing-end-${i}`}
                                />
                                <select
                                    value={b.platform || 'google_reviews'}
                                    onChange={(e) => updateBlock(i, { platform: e.target.value })}
                                    disabled={!timingEnabled}
                                    className="flex-1 min-w-0 h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm px-2"
                                    data-testid={`timing-platform-${i}`}
                                >
                                    {PLATFORMS.map(p => (
                                        <option key={p.value} value={p.value}>{p.label}</option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    onClick={() => removeBlock(i)}
                                    disabled={!timingEnabled}
                                    className="w-7 h-7 shrink-0 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-40"
                                    data-testid={`timing-remove-${i}`}
                                    data-insight-id={i === 0 ? 'fleet.schedule.delete-block' : undefined}
                                    data-insight-label={i === 0 ? 'Delete timing block' : undefined}
                                    aria-label="Remove block"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <div className="flex flex-wrap gap-1">
                                    {DAYS.map((d) => {
                                        const on = (b.days || []).includes(d.code);
                                        return (
                                            <button
                                                key={d.code}
                                                type="button"
                                                onClick={() => toggleDay(i, d.code)}
                                                disabled={!timingEnabled}
                                                className={
                                                    'h-7 w-7 rounded-md text-xs font-bold border transition-colors disabled:opacity-40 ' +
                                                    (on
                                                        ? 'bg-cyan-500 text-white border-cyan-500'
                                                        : 'bg-white dark:bg-slate-900 text-slate-500 border-slate-200 dark:border-slate-700 hover:border-cyan-400')
                                                }
                                                data-testid={`timing-day-${i}-${d.code}`}
                                                aria-pressed={on}
                                                title={d.code}
                                            >
                                                {d.short}
                                            </button>
                                        );
                                    })}
                                </div>
                                <DayGroupHelpers
                                    disabled={!timingEnabled}
                                    blockIndex={i}
                                    onSet={(days) => updateBlock(i, { days })}
                                />
                            </div>

                            {/* v4.15.0 — Inline issue / conflict surfacing */}
                            {meta.issues.length > 0 && (
                                <div
                                    className="rounded-md border border-amber-300/70 bg-amber-50/80 dark:bg-amber-950/30 px-2.5 py-1.5 text-[11px] text-amber-700 dark:text-amber-200"
                                    data-testid={`timing-issues-${i}`}
                                >
                                    {meta.issues.map((iss, k) => <div key={k}>• {iss.message}</div>)}
                                </div>
                            )}
                            {meta.conflicts.length > 0 && (
                                <ConflictBanner
                                    conflicts={meta.conflicts}
                                    onApplyFix={applyFix}
                                />
                            )}
                        </div>
                        );
                    })}
                </div>

                {/* v4.15.0 — Live schedule health summary */}
                {timingEnabled && blocks.length > 0 && (
                    <div
                        className={
                            'mt-3 rounded-md border-2 px-3 py-2 text-xs flex items-center justify-between gap-2 ' +
                            (analysis.hasConflicts
                                ? 'border-red-400/70 bg-red-50/80 dark:bg-red-950/30 text-red-700 dark:text-red-200'
                                : analysis.hasIssues
                                ? 'border-amber-400/70 bg-amber-50/80 dark:bg-amber-950/30 text-amber-700 dark:text-amber-200'
                                : 'border-emerald-400/60 bg-emerald-50/70 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-200')
                        }
                        data-testid="timing-health-pill"
                    >
                        <div className="font-semibold">
                            {analysis.hasConflicts
                                ? `${analysis.conflictRegions.length} conflict${analysis.conflictRegions.length === 1 ? '' : 's'} — fix before saving`
                                : analysis.hasIssues
                                ? 'Some blocks need a destination or days'
                                : 'Schedule is clean and ready to apply'}
                        </div>
                        <div className="text-[10px] opacity-80">
                            {blocks.length} block{blocks.length === 1 ? '' : 's'}
                        </div>
                    </div>
                )}

                <div className="flex flex-col sm:flex-row gap-2 mt-3">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addBlock}
                        disabled={!timingEnabled}
                        data-testid="timing-add-block"
                        data-insight-id="fleet.schedule.add-block"
                        data-insight-label="Add timing block"
                        className="flex-1"
                    >
                        <Plus className="w-3.5 h-3.5 mr-1" />
                        {t('timing.addBlock', 'Add time block')}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setApplyOpen(true)}
                        disabled={analysis.hasConflicts || analysis.hasIssues}
                        data-testid="timing-apply-btn"
                        className="flex-1"
                        title={
                            analysis.hasConflicts
                                ? 'Resolve conflicts before applying'
                                : analysis.hasIssues
                                ? 'Complete each block before applying'
                                : ''
                        }
                    >
                        <Send className="w-3.5 h-3.5 mr-1" />
                        {t('timing.applyTo', 'Apply to…')}
                    </Button>
                </div>

                <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                    {t(
                        'timing.hint',
                        "Times use this card's schedule timezone. Overnight ranges (e.g. 22:00 → 08:00) are supported natively — just enter them as a single block."
                    )}
                </p>
            </div>

            {/* Apply scope dialog */}
            <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
                <DialogContent data-testid="timing-apply-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Send className="w-4 h-4 text-cyan-500" />
                            {t('timing.applyTitle', 'Apply Timing Configuration')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('timing.applyDesc', 'Copy this timing configuration to other cards. Choose a scope.')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2" role="radiogroup">
                        {[
                            { v: 'card', l: t('timing.scopeCard', 'This card only') },
                            { v: 'cards', l: t('timing.scopeCards', 'Selected cards (advanced — use API)'), disabled: true },
                            { v: 'location', l: t('timing.scopeLocation', `All cards in this location`) + (selectedCard?.location_name ? ` (${selectedCard.location_name})` : '') },
                        ].map(opt => (
                            <button
                                key={opt.v}
                                type="button"
                                role="radio"
                                aria-checked={applyScope === opt.v}
                                onClick={() => !opt.disabled && setApplyScope(opt.v)}
                                disabled={opt.disabled}
                                data-testid={`timing-scope-${opt.v}`}
                                className={
                                    'w-full text-left px-3 py-2.5 rounded-md border-2 text-sm transition-colors disabled:opacity-50 ' +
                                    (applyScope === opt.v
                                        ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-950/30 text-cyan-700 dark:text-cyan-300 font-semibold'
                                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-cyan-300')
                                }
                            >
                                {applyScope === opt.v && <Check className="w-3.5 h-3.5 inline mr-1.5" />}
                                {opt.l}
                            </button>
                        ))}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setApplyOpen(false)} disabled={applying}>
                            <X className="w-3.5 h-3.5 mr-1" />
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button
                            onClick={submitApply}
                            disabled={applying}
                            data-testid="timing-apply-submit"
                            className="bg-cyan-600 hover:bg-cyan-700"
                        >
                            {applying ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                            {t('timing.applyCta', 'Apply')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
